import { useState, useEffect } from 'react';

interface Variant {
  name: string;
  key: string;
  stock: number;
  unlimited: boolean;
}

interface InventoryData {
  product_id: string;
  is_made_to_order: boolean;
  stock_quantity: number;
  unlimited_stock: boolean;
  custom_message: string;
  low_stock_threshold: number;
  variants: Variant[];
}

interface StockDisplayProps {
  productId: string;
  onStockChange: (stockInfo: { available: boolean }) => void;
  className?: string;
}

export default function StockDisplay({ productId, onStockChange, className = '' }: StockDisplayProps) {
  const [stock, setStock] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStock();
  }, [productId]);

  const checkStock = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        console.error('Configuration Supabase manquante');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/rest/v1/product_inventory?product_id=eq.${productId}&select=stock_quantity`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const stockQuantity = data[0].stock_quantity;
          setStock(stockQuantity);
          // Toujours disponible, même si stock = 0 (fabrication sur commande)
          onStockChange({ available: true });
        } else {
          setStock(1);
          onStockChange({ available: true });
        }
      } else {
        setStock(1);
        onStockChange({ available: true });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du stock:', error);
      setStock(1);
      onStockChange({ available: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center text-gray-500 ${className}`}>
        <i className="ri-loader-4-line animate-spin mr-2"></i>
        <span style={{ fontFamily: 'Poppins, sans-serif' }}>Vérification du stock...</span>
      </div>
    );
  }

  if (stock === null) {
    return null;
  }

  // Affichage selon le stock
  if (stock > 0) {
    return (
      <div className={`flex items-center text-green-600 ${className}`}>
        <i className="ri-check-line mr-2"></i>
        <span style={{ fontFamily: 'Poppins, sans-serif' }}>
          <strong>{stock} pièce disponible</strong> – expédition rapide
        </span>
      </div>
    );
  } else {
    return (
      <div className={`flex items-center text-[#B97A72] ${className}`}>
        <i className="ri-time-line mr-2"></i>
        <span style={{ fontFamily: 'Poppins, sans-serif' }}>
          <strong>Création réalisée à la main sur commande</strong> – délai de fabrication 7-10 jours
        </span>
      </div>
    );
  }
}

export { type InventoryData };
